import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import './translations/i18n';

// Import components normally but don't render them until needed
import Upload from './Upload';
import ImportURL from './ImportURL';
import AudioRecorder from './AudioRecorder';
import ScreenRecorder from './ScreenRecorder';
import LiveTranscriber from './LiveTranscriber';
import TextToSpeech from './TextToSpeech';
import TabAudioRecorder from './TabAudioRecorder';
import PDFToAudio from './PDFToAudio';
import ImageChat from './ImageChat';
import AIHumanizer from './AIHumanizer';
import AudioCutter from './AudioCutter';
import VoiceTester from './VoiceTester';
import VoiceToApp from './VoiceToApp';
import YouTubeSummarizer from './YouTubeSummarizer';
import LiveTranslator from './LiveTranslator';
import AudioToInvoice from './AudioToInvoice';
import P2PMeeting from './P2PMeeting';
import AskYouTube from './AskYouTube';
import WebcamMicTester from './WebcamMicTester';

// Loading component with a nice animation
const LoadingComponent = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {t('common.loading')}
      </Typography>
    </Box>
  );
};

// Error boundary component
interface ErrorBoundaryProps {
  children: React.ReactNode;
  t: (key: string) => string;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    const { children, t } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            {t('common.somethingWentWrong')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common.tryRefreshing')}
          </Typography>
        </Box>
      );
    }

    return children;
  }
}

interface Tool {
  key: string;
  name: string;
  description: string;
}

interface MatchingTool {
  key: string;
  Component: React.ComponentType<any>;
}

const TOOLS: { [key: string]: React.ComponentType<any> } = {
  upload: Upload,
  importurl: ImportURL,
  audiorecorder: AudioRecorder,
  screenrecorder: ScreenRecorder,
  transcriber: LiveTranscriber,
  texttospeech: TextToSpeech,
  tabaudiorecorder: TabAudioRecorder,
  pdftoaudio: PDFToAudio,
  imagechat: ImageChat,
  aihumanizer: AIHumanizer,
  audiocutter: AudioCutter,
  voicetester: VoiceTester,
  voicetoapp: VoiceToApp,
  youtubesummarizer: YouTubeSummarizer,
  translator: LiveTranslator,
  audiotoinvoice: AudioToInvoice,
  p2pmeeting: P2PMeeting,
  askyoutube: AskYouTube,
  webcammictester: WebcamMicTester,
};

const TOOL_INFO: Tool[] = [
  {
    key: 'upload',
    name: 'tools.upload.name',
    description: 'tools.upload.description',
  },
  {
    key: 'importurl',
    name: 'tools.importurl.name',
    description: 'tools.importurl.description',
  },
  {
    key: 'audiorecorder',
    name: 'tools.audiorecorder.name',
    description: 'tools.audiorecorder.description',
  },
  {
    key: 'screenrecorder',
    name: 'tools.screenrecorder.name',
    description: 'tools.screenrecorder.description',
  },
  {
    key: 'transcriber',
    name: 'tools.transcriber.name',
    description: 'tools.transcriber.description',
  },
  {
    key: 'texttospeech',
    name: 'tools.texttospeech.name',
    description: 'tools.texttospeech.description',
  },
  {
    key: 'tabaudiorecorder',
    name: 'tools.tabaudiorecorder.name',
    description: 'tools.tabaudiorecorder.description',
  },
  {
    key: 'pdftoaudio',
    name: 'tools.pdftoaudio.name',
    description: 'tools.pdftoaudio.description',
  },
  {
    key: 'imagechat',
    name: 'tools.imagechat.name',
    description: 'tools.imagechat.description',
  },
  {
    key: 'aihumanizer',
    name: 'tools.aihumanizer.name',
    description: 'tools.aihumanizer.description',
  },
  {
    key: 'audiocutter',
    name: 'tools.audiocutter.name',
    description: 'tools.audiocutter.description',
  },
  {
    key: 'voicetester',
    name: 'tools.voicetester.name',
    description: 'tools.voicetester.description',
  },
  {
    key: 'voicetoapp',
    name: 'tools.voicetoapp.name',
    description: 'tools.voicetoapp.description',
  },
  {
    key: 'youtubesummarizer',
    name: 'tools.youtubesummarizer.name',
    description: 'tools.youtubesummarizer.description',
  },
  {
    key: 'translator',
    name: 'tools.translator.name',
    description: 'tools.translator.description',
  },
  {
    key: 'audiotoinvoice',
    name: 'tools.audiotoinvoice.name',
    description: 'tools.audiotoinvoice.description',
  },
  {
    key: 'p2pmeeting',
    name: 'tools.p2pmeeting.name',
    description: 'tools.p2pmeeting.description',
  },
  {
    key: 'askyoutube',
    name: 'tools.askyoutube.name',
    description: 'tools.askyoutube.description',
  },
  {
    key: 'webcammictester',
    name: 'tools.webcammictester.name',
    description: 'tools.webcammictester.description',
  },
];

const MicroTool: React.FC = () => {
  const { t } = useTranslation();
  const { toolName } = useParams<{ toolName: string }>();
  const toolKeys = toolName?.toLowerCase().split(',') || [];
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!toolName) {
      document.title = 'Micro Tools | ScreenApp';
      setIsLoading(false);
      return;
    }

    const matchingTool = TOOL_INFO.find(
      (tool) => tool.key.toLowerCase() === toolKeys[0]?.trim().toLowerCase()
    );

    if (matchingTool) {
      document.title = `${t(matchingTool.name)} | ScreenApp`;
    } else {
      document.title = 'Tool Not Found | ScreenApp';
    }

    setIsLoading(false);
  }, [toolName, toolKeys, t]);

  if (!toolName) {
    return (
      <Box sx={{ px: 6, py: 8 }}>
        <Typography variant="h4" gutterBottom>
          {t('common.availableTools')}
        </Typography>
        <Grid container spacing={3}>
          {TOOL_INFO.map((tool) => (
            <Grid item xs={12} sm={6} md={4} key={tool.key}>
              <Card
                component={Link}
                to={`/tool/${tool.key}`}
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[4],
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t(tool.name)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(tool.description)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Find matching tools
  const matchingTools = toolKeys
    .map((toolKey) => {
      const matchingKey = Object.keys(TOOLS).find(
        (key) => key.toLowerCase() === toolKey.trim().toLowerCase()
      );
      return matchingKey
        ? { key: matchingKey, Component: TOOLS[matchingKey] }
        : null;
    })
    .filter((tool): tool is MatchingTool => tool !== null);

  if (matchingTools.length === 0) {
    return <div>{t('common.noValidTools')}</div>;
  }

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <Box
      sx={{
        py: 6,
        px: {
          xs: 0,
          sm: 2,
        },
      }}
    >
      <Grid container spacing={3} alignItems="center">
        {matchingTools.map(({ key, Component }, index) => (
          <React.Fragment key={key}>
            {index > 0 && (
              <Grid item xs={12}>
                <Typography align="center" color="text.secondary">
                  {t('common.or')}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <ErrorBoundary t={t}>
                <Component />
              </ErrorBoundary>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
};

export default MicroTool;
